'use client';

import { useEffect, useMemo, useState } from 'react';
import { useGetPromotionPlans } from '@/lib/api/hooks';
import { useI18n, getLocalizedField } from '@/i18n';
import ListingPickerModal from './ListingPickerModal';

/** 位置图标 */
const PLAN_TYPE_ICON = {
    bump: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
            <path d="M12 19V5M5 12l7-7 7 7" />
        </svg>
    ),
    pin: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
        </svg>
    ),
    homepage: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M3 9h18M9 21V9" />
        </svg>
    ),
};

/** 位置描述 → 运行时根据语言动态生成 */
function buildPlanTypeDesc(t) {
    return {
        bump: t('promote.planTypes.bump') + ' — ' + t('promote.planTypes.bumpDesc'),
        pin: t('promote.planTypes.pin') + ' — ' + t('promote.planTypes.pinDesc'),
        homepage: t('promote.planTypes.homepage') + ' — ' + t('promote.planTypes.homepageDesc'),
    };
}

/** 占位符替换 */
function fmt(str, params) {
    return Object.entries(params || {}).reduce((s, [k, v]) => s.replace(new RegExp(`\\{${k}\\}`, 'g'), v), str);
}

/** 兜底 mock（planTypeName + features 运行时读 i18n） */
function buildFallbackPlans(t) {
    const p = (k) => t(`promote.planTypes.${k}`);
    return [
        {
            id: 'fb-1', planType: 'bump', planTypeName: p('bump'), planNameEn: 'Bump 7d',
            durationDays: 7, priceUsd: 10,
            features: [t('promote.pricing.fb1f1'), t('promote.pricing.fb1f2'), t('promote.pricing.fb1f3'), t('promote.pricing.fb1f4')],
            sort: 1, status: 1,
        },
        {
            id: 'fb-2', planType: 'pin', planTypeName: p('pin'), planNameEn: 'Pin 14d',
            durationDays: 14, priceUsd: 30,
            features: [t('promote.pricing.fb2f1'), t('promote.pricing.fb2f2'), t('promote.pricing.fb2f3'), t('promote.pricing.fb2f4'), t('promote.pricing.fb2f5')],
            sort: 2, status: 1,
        },
        {
            id: 'fb-3', planType: 'homepage', planTypeName: p('homepage'), planNameEn: 'Home 30d',
            durationDays: 30, priceUsd: 80,
            features: [t('promote.pricing.fb3f1'), t('promote.pricing.fb3f2'), t('promote.pricing.fb3f3'), t('promote.pricing.fb3f4'), t('promote.pricing.fb3f5')],
            sort: 3, status: 1,
        },
    ];
}

function buildFAQs(t) {
    return [
        { q: t('promote.faq.q1'), a: t('promote.faq.a1') },
        { q: t('promote.faq.q2'), a: t('promote.faq.a2') },
        { q: t('promote.faq.q3'), a: t('promote.faq.a3') },
        { q: t('promote.faq.q4'), a: t('promote.faq.a4') },
    ];
}

const PAYMENTS = ['PayPal'];

/* ---------- utils ---------- */
function parseFeatures(features) {
    if (!features) return [];
    if (Array.isArray(features)) return features.map((f) => String(f).trim()).filter(Boolean);
    if (typeof features === 'string') return features.split(/[;；]/).map((f) => f.trim()).filter(Boolean);
    return [];
}
function formatPrice(plan) {
    if (plan.priceUsd && plan.priceUsd > 0) return `$${plan.priceUsd}`;
    if (plan.priceGnf && plan.priceGnf > 0) return `${plan.priceGnf} GNF`;
    return `$${plan.priceUsd ?? 0}`;
}
function extractPlans(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (raw.data) {
        if (Array.isArray(raw.data)) return raw.data;
        if (Array.isArray(raw.data.records)) return raw.data.records;
    }
    if (raw.records && Array.isArray(raw.records)) return raw.records;
    return [];
}
function normalizePlans(raw) {
    return raw.filter((p) => p && Number(p.status) === 1).sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
}

function getAuthHeaders() {
    if (typeof window === 'undefined') return {};
    const token = localStorage.getItem('guineemall_access_token');
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
}

/** 创建一个带统一样式的弹窗 overlay 节点 */
function makeOverlay({ title, subtitle, showSpinner = false, accentColor = '#111827' }) {
    const overlay = document.createElement('div');
    const modal = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(10,14,24,0.6);backdrop-filter:blur(2px);display:flex;align-items:center;justify-content:center;z-index:9999';
    modal.style.cssText = 'display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;min-width:260px;max-width:420px;padding:24px 28px;border-radius:16px;background:#fff;box-shadow:0 18px 50px rgba(0,0,0,0.2);text-align:center;color:#111827';

    if (showSpinner) {
        const spin = document.createElement('div');
        spin.style.cssText = 'width:46px;height:46px;border-radius:50%;border:4px solid #e5e7eb;border-top-color:#2563eb;animation:spin 0.9s linear infinite';
        modal.appendChild(spin);
    } else {
        const icon = document.createElement('div');
        icon.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="${accentColor}" stroke-width="1.8" width="44" height="44"><circle cx="12" cy="12" r="9"/><path d="M8 8l8 8M16 8l-8 8" stroke-linecap="round"/></svg>`;
        modal.appendChild(icon);
    }
    const tNode = document.createElement('div');
    tNode.style.cssText = 'font-size:16px;font-weight:700;letter-spacing:0.2px';
    tNode.textContent = title;
    modal.appendChild(tNode);
    const sNode = document.createElement('div');
    sNode.style.cssText = 'font-size:13px;color:#6b7280;line-height:1.5';
    sNode.textContent = subtitle;
    modal.appendChild(sNode);

    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    return { overlay, modal };
}

export default function PromotePage() {
    const { t, lang } = useI18n();
    const [pickerOpen, setPickerOpen] = useState(false);
    const [activePlan, setActivePlan] = useState(null);
    const [paypalConfig, setPaypalConfig] = useState({ clientId: '', mode: 'sandbox', currency: 'USD', redirectUrl: '' });
    const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
    const [paymentProgress, setPaymentProgress] = useState('');

    const FALLBACK_PLANS = useMemo(() => buildFallbackPlans(t), [t]);
    const FAQS = useMemo(() => buildFAQs(t), [t]);

    useEffect(() => {
        const loadPaypalConfig = async () => {
            try {
                const res = await fetch('/api/paypal/config', {
                    method: 'GET',
                    headers: { ...getAuthHeaders() },
                });
                if (!res.ok) return;
                const data = await res.json();
                if (data) {
                    setPaypalConfig({
                        clientId: data.clientId || '',
                        mode: data.mode || 'sandbox',
                        currency: data.currency || 'USD',
                        redirectUrl: data.redirectUrl || '',
                    });
                }
            } catch (err) {
                console.warn('Failed to load PayPal config:', err);
            }
        };
        loadPaypalConfig();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');
        const payerId = params.get('PayerID');
        if (!token && !payerId) return;

        if (token && !payerId) {
            sessionStorage.removeItem('promotion_transaction_ref');
            const { overlay } = makeOverlay({
                title: t('promote.cancelModal.title'),
                subtitle: t('promote.cancelModal.subtitle'),
                accentColor: '#B45309',
            });
            setTimeout(() => {
                overlay.remove();
                window.history.replaceState({}, '', window.location.pathname);
                window.location.reload();
            }, 2000);
            return;
        }

        const fallbackOrderId = sessionStorage.getItem('promotion_transaction_ref') || token;
        const loadingOverlay = makeOverlay({
            title: t('promote.payingModal.title'),
            subtitle: t('promote.payingModal.subtitle'),
            showSpinner: true,
        }).overlay;

        fetch('/api/paypal/capture', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({ token, orderId: fallbackOrderId, paypalOrderId: fallbackOrderId, payerId }),
        })
            .then(async (res) => {
                const result = await res.json().catch(() => ({}));
                const resultData = result?.data || {};
                if (!res.ok) throw new Error(resultData?.message);
                const status = Number(resultData?.status ?? 0);

                if (status === 1 || status === 2) {
                    loadingOverlay.remove();
                    window.history.replaceState({}, '', window.location.pathname);
                    sessionStorage.removeItem('promotion_transaction_ref');
                    const { overlay } = makeOverlay({
                        title: t('promote.successModal.title'),
                        subtitle: t('promote.successModal.subtitle'),
                        showSpinner: true,
                    });
                    setTimeout(() => { overlay.remove(); window.location.href = '/account?tab=orders'; }, 2800);
                    return;
                }
                loadingOverlay.remove();
                alert(resultData?.message || t('promote.alerts.statusUnknown'));
            })
            .catch((err) => {
                loadingOverlay.remove();
                console.error('Capture order error:', err);
                alert(err?.message || t('promote.alerts.captureFail'));
            });
    }, [t]);

    const rawPlans = useGetPromotionPlans();

    const finalPlans = useMemo(() => {
        const list = normalizePlans(extractPlans(rawPlans?.data));
        return list.length > 0 ? list : FALLBACK_PLANS;
    }, [rawPlans?.data, FALLBACK_PLANS]);

    const popularPlan = useMemo(() => {
        if (finalPlans.length === 0) return null;
        return [...finalPlans].sort((a, b) => b.durationDays - a.durationDays)[0];
    }, [finalPlans]);

    const openPicker = (plan) => {
        setActivePlan(plan);
        setPickerOpen(true);
    };

    const handlePicked = async (listing, plan) => {
        if (!listing || !plan) return;
        const pr = t('promote.processing');

        setPickerOpen(false);
        setIsSubmittingOrder(true);
        setPaymentProgress(pr.createOrder);

        try {
            const response = await fetch('/api/promotion/order', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ planId: plan.id, listingId: listing.id }),
            });

            const data = await response.json().catch(() => ({}));
            const resultData = data?.data || data || {};

            if (!response.ok || !resultData.redirectUrl) {
                throw new Error(resultData.message || pr.createOrderFail);
            }

            if (resultData.transactionRef) {
                sessionStorage.setItem('promotion_transaction_ref', String(resultData.transactionRef));
            }

            if (resultData.redirectUrl) {
                setPaymentProgress(pr.redirecting);
                await new Promise((resolve) => setTimeout(resolve, 400));
                window.location.href = resultData.redirectUrl;
                return;
            }
        } catch (error) {
            console.error('[Promote] create order failed:', error);
            setPaymentProgress(pr.createFail);
            alert(error?.message || pr.createOrderFail);
        } finally {
            setIsSubmittingOrder(false);
        }
    };

    const ph = (k) => t(`promote.hero.${k}`);
    const ps = (k) => t(`promote.stats.${k}`);
    const pp = (k) => t(`promote.pricing.${k}`);
    const pm = (k) => t(`promote.payment.${k}`);
    const pfa = (k) => t(`promote.faq.${k}`);
    const pproc = (k) => t(`promote.processing.${k}`);

    return (
        <>
            {(isSubmittingOrder || paymentProgress) && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(10, 14, 24, 0.6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 9999, backdropFilter: 'blur(2px)',
                }}>
                    <div style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        gap: 14, minWidth: 260, maxWidth: 420, padding: '24px 28px',
                        borderRadius: 16, background: '#fff', boxShadow: '0 18px 50px rgba(0,0,0,0.2)',
                        textAlign: 'center', color: '#111827',
                    }}>
                        <div style={{
                            width: 46, height: 46, borderRadius: '50%',
                            border: '4px solid #e5e7eb', borderTopColor: '#2563eb',
                            animation: 'spin 0.9s linear infinite',
                        }} />
                        <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '0.2px' }}>
                            {paymentProgress || pproc('title')}
                        </div>
                        <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
                            {pproc('subtitle')}
                        </div>
                    </div>
                </div>
            )}

            {/* ===== HERO ===== */}
            <section className="promo-hero">
                <div className="promo-hero-grid" />
                <div className="promo-hero-orb promo-hero-orb--1" />
                <div className="promo-hero-orb promo-hero-orb--2" />
                <div className="promo-hero-orb promo-hero-orb--3" />
                <div className="container promo-hero-inner">
                    <div className="promo-hero-eyebrow">
                        <span className="promo-hero-dot" />
                        <span>{ph('eyebrow')}</span>
                    </div>
                    <h1 className="promo-hero-title">
                        {ph('title').split(ph('titleAccent')).map((seg, i, arr) => (
                            i === arr.length - 1 ? (
                                <span key={i}>{seg}</span>
                            ) : (
                                <span key={i}>{seg}<span className="promo-hero-accent">{ph('titleAccent')}</span></span>
                            )
                        ))}
                    </h1>
                    <p className="promo-hero-subtitle">{ph('subtitle')}</p>
                    <div className="promo-hero-cta">
                        <a href="#plans" className="promo-hero-btn promo-hero-btn--primary">{ph('ctaPlans')}</a>
                        <a href="#faq" className="promo-hero-btn promo-hero-btn--ghost">{ph('ctaFaq')}</a>
                    </div>

                    <div className="promo-stats">
                        <div className="promo-stat">
                            <div className="promo-stat-num">4×</div>
                            <div className="promo-stat-label">{ps('avgSpeed')}</div>
                        </div>
                        <div className="promo-stat-sep" />
                        <div className="promo-stat">
                            <div className="promo-stat-num">50×</div>
                            <div className="promo-stat-label">{ps('viewsBoost')}</div>
                        </div>
                        <div className="promo-stat-sep" />
                        <div className="promo-stat">
                            <div className="promo-stat-num">98%</div>
                            <div className="promo-stat-label">{ps('sellerSatisfaction')}</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== PLANS ===== */}
            <section className="promo-section" id="plans">
                <div className="container">
                    <div className="promo-section-head">
                        <div className="section-title-eyebrow">{pp('eyebrow')}</div>
                        <h2 className="section-title">{pp('title')}</h2>
                        <p className="section-subtitle">
                            {rawPlans?.isLoading
                                ? pp('loading')
                                : rawPlans?.error
                                ? pp('errorFallback')
                                : fmt(pp('countFormat'), { count: finalPlans.length })}
                        </p>
                    </div>

                    <div className="promo-plans">
                        {finalPlans.map((p) => {
                            const featuresRaw = getLocalizedField(p, "features", lang) || p.features;
                            const features = parseFeatures(featuresRaw);
                            const planTypeName = getLocalizedField(p, "planTypeName", lang) || p.planTypeName || p.planType;
                            const isPopular = popularPlan && String(p.id) === String(popularPlan.id);
                            const iconSvg = PLAN_TYPE_ICON[p.planType] ?? PLAN_TYPE_ICON.bump;
                            return (
                                <div key={p.id} className={`promo-plan ${isPopular ? 'promo-plan--popular' : ''}`}>
                                    {isPopular && <div className="promo-plan-badge">{pp('popular')}</div>}
                                    <div className="promo-plan-head">
                                        <div className="promo-plan-icon">{iconSvg}</div>
                                        <div className="promo-plan-meta">
                                            <div className="promo-plan-type">{planTypeName}</div>
                                            <div className="promo-plan-dur">{fmt(pp('durationDays'), { days: p.durationDays })}</div>
                                        </div>
                                    </div>
                                    <div className="promo-plan-price">
                                        <span className="promo-plan-price-num">{formatPrice(p).replace('$', '')}</span>
                                        <span className="promo-plan-price-currency">$</span>
                                        <span className="promo-plan-price-period">{fmt(pp('perDays'), { days: p.durationDays })}</span>
                                    </div>
                                    <ul className="promo-plan-features">
                                        {features.map((f, idx) => (
                                            <li key={idx}>
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><polyline points="20 6 9 17 4 12" /></svg>
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        className={`promo-plan-btn ${isPopular ? 'promo-plan-btn--primary' : 'promo-plan-btn--ghost'}`}
                                        onClick={() => openPicker(p)}
                                        disabled={isSubmittingOrder}
                                    >
                                        {isSubmittingOrder ? pp('processing') : pp('chooseThis')}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ===== PAYMENT ===== */}
            <section className="promo-section promo-section--soft">
                <div className="container">
                    <div className="promo-payment">
                        <div className="promo-payment-head">
                            <div className="section-title-eyebrow">{pm('eyebrow')}</div>
                            <h3 className="promo-payment-title">{pm('title')}</h3>
                            <p className="promo-payment-desc">{pm('desc')}</p>
                        </div>
                        <div className="promo-payment-grid">
                            {PAYMENTS.map((name) => (
                                <div key={name} className="promo-pay-chip">
                                    <span className="promo-pay-dot" />
                                    {name}
                                </div>
                            ))}
                        </div>
                        {paypalConfig.clientId && (
                            <div className="promo-payment-meta">
                                <span>{pm('env')} {paypalConfig.mode}</span>
                                <span>{pm('currency')} {paypalConfig.currency}</span>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* ===== FAQ ===== */}
            <section className="promo-section" id="faq">
                <div className="container">
                    <div className="promo-section-head">
                        <div className="section-title-eyebrow">{pfa('eyebrow')}</div>
                        <h2 className="section-title">{pfa('title')}</h2>
                    </div>
                    <div className="promo-faq">
                        {FAQS.map((f, i) => (
                            <details key={f.q} className="promo-faq-item">
                                <summary>
                                    <span className="promo-faq-num">0{i + 1}</span>
                                    <span>{f.q}</span>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><polyline points="6 9 12 15 18 9" /></svg>
                                </summary>
                                <p>{f.a}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== Listing Picker Modal ===== */}
            <ListingPickerModal
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                plan={activePlan}
                onConfirm={handlePicked}
            />
        </>
    );
}
